/**
 * REVISIÓN HUMANA DE CONFLICTOS OFFLINE (FASE 5.5)
 *
 * Capa de negocio sobre `offline/db.ts`, mismo criterio de separación que
 * `offline/mutaciones.ts` (validación) y `offline/sync.ts` (red): aquí vive
 * lo que hace falta para transformar una mutación EN_ESPERA en una decisión
 * humana segura —
 *
 *   · consultar el estado FRESCO del servidor (§8), vía los dos endpoints
 *     de sólo lectura de FASE 5.5 (`GET /api/sync/lineas/:id`,
 *     `GET /api/sync/no-conformidades/:id`) — nunca la proyección local,
 *     que es UX, no autoridad (§10);
 *   · decidir si la acción TODAVÍA tiene sentido reintentarla, derivado
 *     SIEMPRE de esa respuesta fresca (`accionesPermitidas` para línea,
 *     el propio `estadoActual` para NC) — nunca una máquina de transición
 *     nueva ni divergente (§10);
 *   · nunca decide "requiere CONSULTOR" para una mutación RECHAZADA o
 *     DEPENDENCIA_BLOQUEADA por su cuenta: esas tienen su propio camino
 *     (§17, §13/§14) — esta clasificación es SÓLO para CONFLICTO_REVISION.
 */
import { api } from '../api.ts';
import { esTipoMutacionLinea, estadosOrigenDe } from './proyeccion.ts';
import { resolverIdEntidadReal, type Mutacion, type TipoMutacionLinea } from './db.ts';

export interface AccionesPermitidasLinea {
  readonly confirmar: boolean;
  readonly verificar: boolean;
  readonly noOk: boolean;
  readonly noAplica: boolean;
}

export interface EstadoFrescoLinea {
  readonly idLinea: string;
  readonly idOt: string;
  readonly estadoOt: string;
  readonly estadoLinea: string;
  readonly revisionActual: string | null;
  readonly accionesPermitidas: AccionesPermitidasLinea;
}

export interface EstadoFrescoNc {
  readonly idNc: string;
  readonly idOt: string;
  readonly estadoActual: string;
  readonly revisionActual: string;
}

/** §8 — "obtener estado fresco servidor". Nunca cacheado: cada llamada es una consulta nueva, a propósito (el usuario puede tardar en decidir). */
export async function obtenerEstadoFrescoLinea(idLinea: string): Promise<EstadoFrescoLinea> {
  return api.get<EstadoFrescoLinea>(`/api/sync/lineas/${idLinea}`);
}

export async function obtenerEstadoFrescoNc(idNc: string): Promise<EstadoFrescoNc> {
  return api.get<EstadoFrescoNc>(`/api/sync/no-conformidades/${idNc}`);
}

const CAMPO_ACCION_LINEA: Readonly<Record<TipoMutacionLinea, keyof AccionesPermitidasLinea>> = {
  LINEA_CONFIRMAR: 'confirmar',
  LINEA_VERIFICAR: 'verificar',
  LINEA_NO_OK: 'noOk',
  LINEA_NO_APLICA: 'noAplica',
};

const TIPOS_NC_TRANSICION = new Set(['NC_INICIAR_TRATAMIENTO', 'NC_CERRAR']);

export type EvaluacionReintento =
  | { readonly retryable: true; readonly ambito: 'LINEA'; readonly estadoFresco: EstadoFrescoLinea }
  | { readonly retryable: true; readonly ambito: 'NC'; readonly estadoFresco: EstadoFrescoNc }
  | { readonly retryable: false; readonly motivo: string };

const MOTIVO_REQUIERE_CONSULTOR = 'Esta acción ya no puede reintentarse sola — requiere revisión de un consultor.';

/**
 * §9/§10 — "qué acciones pueden reintentarse", derivado del estado FRESCO
 * del servidor, nunca de la proyección local. Sólo evalúa mutaciones
 * `CONFLICTO_REVISION`: una `DEPENDENCIA_BLOQUEADA` no tiene nada con qué
 * comparar (la entidad de la que depende puede ni existir todavía en el
 * servidor — se resuelve siempre a través de su mutación padre, §13/§14) y
 * una RECHAZADA no ofrece este camino en absoluto (§17).
 */
export async function evaluarReintento(idPerfil: string, m: Mutacion): Promise<EvaluacionReintento> {
  if (m.estadoSync !== 'EN_ESPERA' || m.razonEspera !== 'CONFLICTO_REVISION') {
    return { retryable: false, motivo: MOTIVO_REQUIERE_CONSULTOR };
  }

  if (esTipoMutacionLinea(m.tipo)) {
    const estadoFresco = await obtenerEstadoFrescoLinea(m.idEntidad);
    const campo = CAMPO_ACCION_LINEA[m.tipo];
    if (!estadoFresco.accionesPermitidas[campo]) {
      return { retryable: false, motivo: MOTIVO_REQUIERE_CONSULTOR };
    }
    return { retryable: true, ambito: 'LINEA', estadoFresco };
  }

  if (TIPOS_NC_TRANSICION.has(m.tipo)) {
    //  `m.idEntidad` puede ser todavía el marcador local de una NC creada
    //  offline (ver `resolverIdEntidadReal`) — resolver el id real ANTES
    //  de consultar el servidor, nunca enviar el marcador como si fuera un
    //  UUID de verdad.
    const idNcReal = await resolverIdEntidadReal(idPerfil, m);
    const estadoFresco = await obtenerEstadoFrescoNc(idNcReal);
    const puede =
      m.tipo === 'NC_INICIAR_TRATAMIENTO' ? estadoFresco.estadoActual === 'ABIERTA' : estadoFresco.estadoActual === 'EN_TRATAMIENTO';
    if (!puede) return { retryable: false, motivo: MOTIVO_REQUIERE_CONSULTOR };
    return { retryable: true, ambito: 'NC', estadoFresco };
  }

  //  NC_CREAR nunca genera CONFLICTO_REVISION (no hay revisión previa con
  //  qué comparar, `sync/servicio.ts::procesarMutacionNc`) y EVIDENCIA_SUBIR
  //  nunca queda EN_ESPERA (un bloqueo de dependencia resuelve directo a
  //  RECHAZADA, `offline/sync.ts::subirUnaEvidencia`) — llegar aquí sería
  //  un estado que el modelo actual no produce; tratarlo como no
  //  reintentable es lo seguro.
  return { retryable: false, motivo: MOTIVO_REQUIERE_CONSULTOR };
}

/** §6/§30 — el estado que el DISPOSITIVO conocía cuando registró la acción, en texto llano. Para línea, deriva de la propia máquina (`estadosOrigenDe`); para NC, de la transición pedida — nunca un código técnico. */
export function estadoLocalConocido(m: Mutacion): string {
  if (esTipoMutacionLinea(m.tipo)) {
    //  F14.2 — el estado exacto de partida, si se guardó al registrar; si no (operación anterior), CONFIRMAR sólo
    //  podía partir de PENDIENTE (REHACER offline no existía).
    if (m.estadoOrigenLocal != null) return m.estadoOrigenLocal;
    if (m.tipo === 'LINEA_CONFIRMAR') return 'PENDIENTE';
    const origenes = estadosOrigenDe(m.tipo);
    return origenes.join(' o ');
  }
  if (m.tipo === 'NC_INICIAR_TRATAMIENTO') return 'ABIERTA';
  if (m.tipo === 'NC_CERRAR') return 'EN_TRATAMIENTO';
  return '—';
}
